import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shortLinkService } from '../../../utils/storage';
import { useToast } from '../../../hooks/useToast';
import { useSubscription } from '../../../hooks/useSubscription';

interface ShortLinkModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function ShortLinkModal({ onClose, onSuccess }: ShortLinkModalProps) {
  const { t } = useTranslation('dashboard');
  const { showToast } = useToast();
  const { canCreateLink } = useSubscription();
  const [name, setName] = useState('');
  const [originalUrl, setOriginalUrl] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [loading, setLoading] = useState(false);

  const generateShortCode = () => {
    const code = Math.random().toString(36).substring(2, 8);
    setShortCode(code);
  };

  const handleSave = async () => {
    if (!name || !originalUrl || !shortCode) {
      showToast('모든 필드를 입력해주세요', 'error');
      return;
    }

    if (!canCreateLink()) {
      showToast('짧은 링크 생성 한도에 도달했습니다. 프로 플랜으로 업그레이드하세요.', 'error');
      return;
    }

    setLoading(true);
    try {
      await shortLinkService.create({
        name,
        original_url: originalUrl,
        short_code: shortCode,
      });

      showToast('짧은 링크가 생성되었습니다', 'success');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('짧은 링크 저장 실패:', error);
      showToast(error.message || '짧은 링크 생성에 실패했습니다', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">{t('link.create')}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('link.name')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="예: 프로모션 링크"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('link.original')}
            </label>
            <input
              type="url"
              value={originalUrl}
              onChange={(e) => setOriginalUrl(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="https://example.com/very/long/url"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('link.short')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={shortCode}
                onChange={(e) => setShortCode(e.target.value)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="abc123"
              />
              <button
                onClick={generateShortCode}
                className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line mr-2"></i>
                자동 생성
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {window.location.origin}/s/{shortCode || '...'}
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {t('action.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <i className="ri-loader-4-line animate-spin"></i>}
              {loading ? '생성 중...' : t('action.create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
